import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Clapperboard, Coins, Infinity as InfinityIcon, AlertTriangle } from "lucide-react";

export const VideoCreditsMeter = () => {
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get("/grow/video/credits").then((r) => setC(r.data)).catch(() => setErr(true));
  }, []);

  if (err) return null;
  if (!c) return null;

  const used = c.allowance_used || 0;
  const total = c.monthly_free || 0;
  const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border border-border/50 bg-card p-5" data-testid="video-credits-meter">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Clapperboard className="w-4 h-4" /></div>
          <span className="text-xs uppercase tracking-widest text-muted-foreground">Video credits</span>
        </div>
        <Button variant="ghost" size="sm" onClick={() => navigate("/video-studio")} className="text-primary hover:text-primary h-7" data-testid="video-meter-open-btn">Open Studio →</Button>
      </div>

      {c.is_admin ? (
        <div className="flex items-center gap-2 font-heading text-2xl font-black" data-testid="video-meter-unlimited"><InfinityIcon className="w-6 h-6 text-primary" /> Unlimited</div>
      ) : (
        <>
          <div className="flex items-end justify-between mb-2">
            <div>
              <span className="font-heading text-3xl font-black text-primary" data-testid="video-meter-remaining">{c.free_remaining}</span>
              <span className="text-sm text-muted-foreground"> / {total} free sec left</span>
            </div>
            <div className="flex items-center gap-1 text-sm text-muted-foreground"><Coins className="w-3.5 h-3.5 text-primary" /> <b className="text-foreground" data-testid="video-meter-balance">{c.balance}</b> credits</div>
          </div>
          <Progress value={pct} className="h-2" data-testid="video-meter-progress" />
          <div className="text-xs text-muted-foreground mt-2">{used} of {total} monthly seconds used{c.plan ? ` · ${c.plan} plan` : ""}. Resets monthly.</div>
          {total > 0 && c.free_remaining <= Math.max(10, Math.round(total * 0.2)) && (c.balance || 0) < 30 && !c.autorefill && (
            <div className="mt-2 text-xs text-amber-400 flex items-center gap-1.5" data-testid="video-meter-low">
              <AlertTriangle className="w-3.5 h-3.5" /> Almost out — top up or enable auto-refill in the Studio.
            </div>
          )}
        </>
      )}
    </motion.div>
  );
};

export default VideoCreditsMeter;
